import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { PhoneInputField } from '../UISystem/Forms/PhoneInputField';
import PillInput from '../UISystem/Forms/PillInput';
import { COLORS, SHADOWS, INPUT_TOKENS } from '../../styles/UISystem';
import { CROWN_BLACK } from '../../styles/brandAssets';
import { writeWaitlistJoin } from '../../services/waitlist';

/**
 * LaunchSignupOverlay Component
 *
 * Three-stage overlay prompting Anon users to get their own QR:
 * - Stage 0: "Get your own QR" with Sign up button and "Remind me later" link
 * - Stage 1: name + NUMBER capture. Number-only (user ruling 2026-08-08:
 *   one identifier — the phone is what the invite ships to, what the
 *   deferred-conversion data pull matches on (waitlist anon_user_id bridge),
 *   and what login uses; a second option just creates "which one did I use?").
 *   The phone-or-email toggle retired with this ruling.
 * - Stage 2: thanks — auto-dismisses after thanksDwellMs, X for fast readers
 *
 * Props:
 * - show: boolean - Controls overlay visibility
 * - onClose: function - Called when overlay is dismissed without completing
 * - onComplete: function({ contactType, contactValue, name }) - Called when user
 *   successfully submits their info. contactType is always 'phone'.
 * - prefillPhone: string - Pre-fill phone if Anon shared it
 * - startAtStage: number - Which stage to start at (0, 1, or 2). Use 1 to skip the intro.
 * - submitButtonText: string - Text for the submit button (default: "Let me know")
 */

const LaunchSignupOverlay = ({
  show,
  onClose,
  onComplete,
  prefillPhone = '',
  prefillName = '',
  startAtStage = 0, // Allow skipping stage 0 (e.g., startAtStage={1} for direct signup)
  submitButtonText = 'Let me know', // Configurable button text for stage 1
  chatRoomId = null, // For checking if already signed up
  connectedWith = null, // Host's name — rides into the launch_waitlist row (D10 tie)
  anonUserId = null, // D7 bridge pointer — joiner's anon identity on the waitlist row
  hideNameField = false, // SP capture (8a): name known from the profile, field hidden
  hideHeader = false, // When true, hides title + valueProp from stage 1 (keeps caption)
  // Configurable copy — defaults preserve the original "Royale / Magic address book"
  // waitlist framing (WelcomePage, AnonConnectionsListPage). AnonConnectionPage
  // overrides these for the "Join Royale" closer.
  title = 'Royale', // Stage 1 heading (28px bold)
  valueProp = 'Magic address book', // Stage 1 value-prop line (18px)
  caption = 'Launching soon', // Stage 1 caption line (16px grey); pass '' to hide
  thanksTitle = 'Signed up!', // Stage 2 heading — '{name}' is replaced with the entered name
  thanksSubtitle = "We'll let you know when launched", // Stage 2 subtitle
  thanksDwellMs = 2000, // Stage 2 auto-dismiss; pass longer for multi-line copy
  // Stage 2 fills the screen instead of the bottom sheet (decision #28) — used
  // when the page behind the overlay is spent (the expired screen): a success
  // card floating over "Your chat has expired" contradicts itself.
  thanksFullscreen = false,
}) => {
  // Stage: 0 = Create Account, 1 = Launching Soon, 2 = Thanks
  const [stage, setStage] = useState(startAtStage);
  const [contactValue, setContactValue] = useState('');
  const [name, setName] = useState('');

  const phoneInputRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const submittedDataRef = useRef({ contactType: null, contactValue: null });

  // Reset state when overlay is shown — skip to success if already signed up
  useEffect(() => {
    if (show) {
      const alreadySignedUp = localStorage.getItem(`analytics_signup_waitlist_${chatRoomId}`);
      setStage(alreadySignedUp ? 2 : startAtStage);
      hasCompletedRef.current = false;
      setName(prefillName);
      setContactValue(prefillPhone || '');
    }
  }, [show, prefillPhone, prefillName, startAtStage, chatRoomId]);

  // Complete once — shared by the auto-dismiss timer and the stage-2 X
  const finishSignup = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    if (onComplete) {
      // Pass submitted contact info to parent for waitlist/analytics
      onComplete(submittedDataRef.current);
    } else {
      onClose();
    }
  }, [onComplete, onClose]);

  // Backdrop dismiss: after a successful submit the join is durable
  // (writeWaitlistJoin fired), so dismissing IS completing — a backdrop tap
  // during the thanks dwell must not skip onComplete (B26: the Get-app banner
  // stayed because the retire flag rode onComplete). A no-submit dismiss —
  // including already-signed-up reopens, whose submittedData is empty — stays
  // a plain close so call-site completion logging never fires with nulls.
  const handleBackdropDismiss = () => {
    if (submittedDataRef.current.contactValue) {
      finishSignup();
    } else {
      onClose();
    }
  };

  // Auto-dismiss the thanks stage — dwell is configurable so multi-line copy
  // is actually readable (device pass 12: 2s was impossible to read)
  useEffect(() => {
    if (stage === 2 && !hasCompletedRef.current) {
      const timer = setTimeout(finishSignup, thanksDwellMs);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [stage, finishSignup, thanksDwellMs]);

  // Focus input when entering stage 1
  useEffect(() => {
    if (show && stage === 1) {
      requestAnimationFrame(() => {
        phoneInputRef.current?.focus();
      });
    }
  }, [show, stage]);

  // Handle submit
  const handleSubmit = () => {
    if (!contactValue.trim() || !name.trim()) return;

    // Store submitted data for onComplete callback
    submittedDataRef.current = { contactType: 'phone', contactValue, name: name.trim() };

    // D10: the durable half of the keep-promise — one write path for every
    // call site (landing, closer, expired). Fire-and-forget: the thanks stage
    // never waits on the network; the service logs its own failures.
    writeWaitlistJoin({
      contactType: 'phone',
      contactValue: contactValue.trim(),
      name: name.trim(),
      chatRoomId,
      connectedWith,
      anonUserId,
    });

    setStage(2);
  };

  // Handle "Create account" or "Remind me later" click
  const handleProceedToSignup = () => {
    setStage(1);
  };

  const isButtonEnabled = contactValue.trim().length > 0 && name.trim().length > 0;

  // Shared input container styles for PhoneInputField (not using PillInput due to embedded mode)
  const phoneContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '50px',
    borderRadius: '25px',
    border: 'none',
    backgroundColor: '#FFFFFF',
    boxShadow: INPUT_TOKENS.PILL_SHADOW,
    overflow: 'hidden',
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="overlay-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pointerEvents: 'auto',
          }}
        >
          {/* Backdrop - click to close (post-submit: completes instead) */}
          <div
            onClick={handleBackdropDismiss}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1,
            }}
          />

          {/* Single persistent card that animates in/out. On the fullscreen
              thanks (decision #28) the sheet grows to cover the page. */}
          <motion.div
            initial={{ y: 300 }}
            animate={{ y: 0 }}
            exit={{ y: 300 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              backgroundColor: '#FFFFFF',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              padding: '40px 20px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: SHADOWS.TIER3,
              ...(stage === 2 && thanksFullscreen
                ? {
                    flex: 1,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    justifyContent: 'center',
                  }
                : {}),
            }}
          >
            {/* Content transitions smoothly within the card */}
            <AnimatePresence mode="wait">
              {/* Stage 0: Download the app */}
              {stage === 0 && (
                <motion.div
                  key="download-app"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  {/* Title */}
                  <div
                    style={{
                      fontSize: '28px',
                      fontWeight: '700',
                      color: '#000',
                      textAlign: 'center',
                      marginBottom: '24px',
                    }}
                  >
                    {title}
                  </div>

                  {/* Download the app Button */}
                  <ButtonRounded
                    text="Download the app"
                    onClick={handleProceedToSignup}
                    size="large"
                  />

                  {/* Remind me later button */}
                  <div style={{ marginTop: '4px' }}>
                    <ButtonRounded
                      text="Remind me later"
                      onClick={handleProceedToSignup}
                      size="large"
                      variant="blank"
                    />
                  </div>
                </motion.div>
              )}

              {/* Stage 1: Launching Soon. Spacing rhythm (device pass 2026-07-21):
                  12px base gap groups the header lockup (crown/title/tagline)
                  and the field pair; the caption's bottom margin makes the one
                  big break (32px) before the form; the button sits at 28px. */}
              {stage === 1 && (
                <motion.div
                  key="launching-soon"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                  }}
                >
                  {!hideHeader && (
                    <>
                      {/* Crown + title — the brand over the ask (mock 2026-07-21;
                          same crown-over-heading grammar as SheetHeader and the
                          stage-2 thanks card) */}
                      <img src={CROWN_BLACK} alt="" style={{ width: '44px', height: '28px' }} />
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: '700',
                          color: '#000',
                          textAlign: 'center',
                        }}
                      >
                        {title}
                      </div>

                      {/* Value prop — hidden when empty */}
                      {valueProp && (
                        <div
                          style={{
                            fontSize: '18px',
                            fontWeight: '400',
                            color: '#000',
                            textAlign: 'center',
                            marginTop: '4px',
                          }}
                        >
                          {valueProp}
                        </div>
                      )}
                    </>
                  )}

                  {/* Subtitle / caption — hidden when caption is empty. Hugs the
                      title (header lockup); its bottom margin is the break
                      before the form (12 gap + 20 = 32) */}
                  {caption && (
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '400',
                        color: '#666',
                        textAlign: 'center',
                        marginBottom: '20px',
                      }}
                    >
                      {caption}
                    </div>
                  )}

                  {/* Name input — hidden for the SP capture (8a: the name is
                      already on the profile; prefillName still feeds submit) */}
                  {!hideNameField && (
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '280px',
                      }}
                    >
                      <PillInput
                        variant="shadow"
                        inputMode="text"
                        autoComplete="name"
                        autoCapitalize="words"
                        placeholder="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fontSize="16px"
                        style={{ height: '50px', borderRadius: '25px' }}
                      />
                    </div>
                  )}

                  {/* Number capture — the ONE identifier (user ruling
                      2026-08-08): the invite ships to it, the deferred
                      conversion matches on it, login uses it. The old
                      phone-or-email toggle retired with the ruling. */}
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '280px',
                    }}
                  >
                    {/* Phone input — embedded PhoneInputField (not PillInput due to embedded mode) */}
                    <div
                      style={{
                        ...phoneContainerStyle,
                        padding: '0 12px',
                      }}
                    >
                      <PhoneInputField
                        ref={phoneInputRef}
                        value={contactValue}
                        onChange={setContactValue}
                        placeholder="Phone"
                        name="phone-number"
                        autoComplete="tel"
                        embedded={true}
                        fontSize={16}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div style={{ marginTop: '16px' }}>
                    <ButtonRounded
                      text={submitButtonText}
                      onClick={handleSubmit}
                      disabled={!isButtonEnabled}
                      size="large"
                    />
                  </div>
                </motion.div>
              )}

              {/* Stage 2: Signed up */}
              {stage === 2 && (
                <motion.div
                  key="thanks"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    minHeight: '100px',
                    gap: '8px',
                  }}
                >
                  {/* X — early dismiss for fast readers; completes the same
                      path the auto-dismiss timer would (device pass 12) */}
                  <button
                    aria-label="Done"
                    onClick={finishSignup}
                    style={{
                      position: 'absolute',
                      top: thanksFullscreen ? 'calc(env(safe-area-inset-top) + 14px)' : '14px',
                      right: '14px',
                      width: '40px',
                      height: '40px',
                      border: 'none',
                      background: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <X size={20} color={COLORS.CHAT_TEXT_DARK} weight="regular" />
                  </button>
                  {/* Crown + bold title + italic subtitle — user mock, device pass 12 */}
                  <img
                    src={CROWN_BLACK}
                    alt=""
                    style={{ width: '44px', height: '28px', marginBottom: '4px' }}
                  />
                  <div
                    style={{
                      fontSize: '17px',
                      fontWeight: '600',
                      color: '#000',
                      textAlign: 'center',
                    }}
                  >
                    {thanksTitle.replace('{name}', name.trim())}
                  </div>
                  <div
                    style={{
                      fontSize: '16px',
                      fontStyle: 'italic',
                      color: '#666',
                      textAlign: 'center',
                      lineHeight: '1.4',
                      marginTop: '8px',
                    }}
                  >
                    {thanksSubtitle}
                  </div>
                  {/* Action-confirmed check (D12) — quiet house gray, not a
                      celebratory green; the words carry the promise */}
                  <Check
                    size={26}
                    weight="bold"
                    color={COLORS.CHAT_TEXT_DARK}
                    style={{ marginTop: '10px' }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LaunchSignupOverlay;
